import React, { useState } from 'react';
import { Button, Stack, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { useOrganizations } from '../contexts/OrganizationContext';
import { useToast } from '../contexts/ToastContext';
import { integrationApiService } from '../services/integrationApi';
import type { FieldNamespaceStatus } from '../services/integrationApi';
import { ConfirmDialog, InlineMessage, SectionCard, StatusChip } from './ui';

export interface ShopifyFieldNamespaceRowProps {
  integrationId: string;
}

const PHASE_TONE = { legacy: 'neutral', dual: 'caution', motzu: 'positive' } as const;

/**
 * "Field namespace" on the Shopify settings (flag `motzu_field_namespace`):
 * where the store is in the move from `custom.*` to `motzu.*` metafields, and,
 * for an owner or admin, the step that finishes it once the theme reads
 * `motzu.*`. Hidden with the flag off.
 */
const ShopifyFieldNamespaceRow: React.FC<ShopifyFieldNamespaceRowProps> = ({ integrationId }) => {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { enabled } = useOrgFeatureFlag('motzu_field_namespace');
  const { actingOrganization } = useOrganizations();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const canComplete = actingOrganization?.role === 'owner' || actingOrganization?.role === 'admin';
  const queryKey = ['shopify-field-namespace', integrationId];

  const status = useQuery<FieldNamespaceStatus>({
    queryKey,
    enabled,
    queryFn: () => integrationApiService.getFieldNamespace(integrationId),
    staleTime: 60 * 1000,
    retry: false,
  });

  const complete = useMutation({
    mutationFn: () => integrationApiService.completeFieldNamespace(integrationId),
    onSuccess: (next) => {
      queryClient.setQueryData(queryKey, next);
      setConfirmOpen(false);
      showToast(t('integrations.fieldNamespace.completed'), 'success');
    },
    onError: (err: Error) => {
      setConfirmOpen(false);
      showToast(err.message || t('integrations.fieldNamespace.completeFailed'), 'error');
    },
  });

  if (!enabled) return null;

  const phase = status.data?.phase ?? 'legacy';
  const missing = status.data?.motzuDefinitions.missing.length ?? 0;

  return (
    <SectionCard
      title={t('integrations.fieldNamespace.title')}
      subtitle={t('integrations.fieldNamespace.subtitle')}
      actions={
        status.data ? (
          <StatusChip label={t(`integrations.fieldNamespace.phase.${phase}`)} tone={PHASE_TONE[phase]} />
        ) : undefined
      }
    >
      <Stack spacing={1.5}>
        {status.isError && (
          <InlineMessage severity="warning">{t('integrations.fieldNamespace.loadFailed')}</InlineMessage>
        )}
        {status.data && (
          <Typography variant="body2" color="text.secondary">
            {t(`integrations.fieldNamespace.explain.${phase}`)}
          </Typography>
        )}
        {status.data && phase === 'dual' && missing > 0 && (
          <InlineMessage severity="info">
            {t('integrations.fieldNamespace.missing', { count: missing })}
          </InlineMessage>
        )}
        {status.data && phase === 'dual' && !status.data.dualPassCompletedAt && (
          <InlineMessage severity="info">{t('integrations.fieldNamespace.waitingForPass')}</InlineMessage>
        )}
        {status.data && phase === 'dual' && canComplete && (
          <div>
            <Button variant="outlined" size="small" onClick={() => setConfirmOpen(true)} disabled={complete.isPending || !status.data.dualPassCompletedAt}>
              {t('integrations.fieldNamespace.complete')}
            </Button>
          </div>
        )}
      </Stack>
      <ConfirmDialog
        open={confirmOpen}
        title={t('integrations.fieldNamespace.confirmTitle')}
        message={t('integrations.fieldNamespace.confirmMessage')}
        confirmLabel={t('integrations.fieldNamespace.complete')}
        cancelLabel={t('common.cancel')}
        confirmColor="warning"
        loading={complete.isPending}
        onConfirm={() => complete.mutate()}
        onClose={() => setConfirmOpen(false)}
      />
    </SectionCard>
  );
};

export default ShopifyFieldNamespaceRow;
